import React, { useState, useEffect } from 'react';
import { X, Send, CheckCircle, Loader2 } from 'lucide-react';

const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxuBl9lFEXSHC3r8XkrKXO-XNPQ5t3MjMjoOQ5Z87191D2s__vMsQXCexCR3zujHKbB/exec';

const ContactFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    launchTimeline: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setFormData({ name: '', email: '', phone: '', company: '', launchTimeline: '' });
        setIsSuccess(false);
        setError('');
      }, 300);
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Please enter your name');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Please enter your email');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.phone.trim()) {
      setError('Please enter your phone number');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setError('');

    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      // Since no-cors doesn't return response, assume success
      setIsSuccess(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-[#04101C]/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div 
        className={`relative w-full max-w-md bg-[#0a1a2e] rounded-3xl shadow-2xl border border-[#00C4CC]/20 overflow-hidden transform transition-all duration-300 ${
          isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
      >
        {/* Gradient Top Border */}
        <div className="h-1 bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE]" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#F5FDFF]/10 flex items-center justify-center text-[#F5FDFF]/60 hover:bg-[#F5FDFF]/20 hover:text-[#F5FDFF] transition-all duration-200"
        >
          <X size={18} />
        </button>

        {/* Content */}
        <div className="p-8">
          {!isSuccess ? (
            <>
              {/* Header */}
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-[#F5FDFF] mb-2">
                  Start Your <span className="bg-gradient-to-r from-[#00C4CC] to-[#00E08F] bg-clip-text text-transparent">Podcast</span>
                </h2>
                <p className="text-[#F5FDFF]/60 text-sm">
                  Fill in your details and we&apos;ll get back to you within 24 hours.
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-[#F5FDFF]/70 text-sm font-medium mb-2">
                    Name <span className="text-[#FF6B7A]">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your full name"
                    className="w-full px-4 py-3 rounded-xl bg-[#F5FDFF]/5 border border-[#F5FDFF]/10 text-[#F5FDFF] placeholder-[#F5FDFF]/30 focus:outline-none focus:border-[#00E08F]/50 focus:ring-1 focus:ring-[#00E08F]/50 transition-all duration-200"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-[#F5FDFF]/70 text-sm font-medium mb-2">
                    Email <span className="text-[#FF6B7A]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 rounded-xl bg-[#F5FDFF]/5 border border-[#F5FDFF]/10 text-[#F5FDFF] placeholder-[#F5FDFF]/30 focus:outline-none focus:border-[#00E08F]/50 focus:ring-1 focus:ring-[#00E08F]/50 transition-all duration-200"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-[#F5FDFF]/70 text-sm font-medium mb-2">
                    Phone Number <span className="text-[#FF6B7A]">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 XXXXX XXXXX"
                    className="w-full px-4 py-3 rounded-xl bg-[#F5FDFF]/5 border border-[#F5FDFF]/10 text-[#F5FDFF] placeholder-[#F5FDFF]/30 focus:outline-none focus:border-[#00E08F]/50 focus:ring-1 focus:ring-[#00E08F]/50 transition-all duration-200"
                  />
                </div>

                {/* Company */}
                <div>
                  <label className="block text-[#F5FDFF]/70 text-sm font-medium mb-2">
                    Company / Brand Name
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Your company or brand"
                    className="w-full px-4 py-3 rounded-xl bg-[#F5FDFF]/5 border border-[#F5FDFF]/10 text-[#F5FDFF] placeholder-[#F5FDFF]/30 focus:outline-none focus:border-[#00E08F]/50 focus:ring-1 focus:ring-[#00E08F]/50 transition-all duration-200"
                  />
                </div>

                {/* Launch Timeline */}
                <div>
                  <label className="block text-[#F5FDFF]/70 text-sm font-medium mb-2">
                    How soon do you want to launch your podcast?
                  </label>
                  <select
                    name="launchTimeline"
                    value={formData.launchTimeline}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl bg-[#F5FDFF]/5 border border-[#F5FDFF]/10 text-[#F5FDFF] focus:outline-none focus:border-[#00E08F]/50 focus:ring-1 focus:ring-[#00E08F]/50 transition-all duration-200 appearance-none cursor-pointer"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23F5FDFF'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                  >
                    <option value="" className="bg-[#04101C] text-[#F5FDFF]/50">Select timeline</option>
                    <option value="ASAP" className="bg-[#04101C]">ASAP</option>
                    <option value="1-2 weeks" className="bg-[#04101C]">1-2 weeks</option>
                    <option value="1 month" className="bg-[#04101C]">1 month</option>
                    <option value="2-3 months" className="bg-[#04101C]">2-3 months</option>
                    <option value="Just exploring" className="bg-[#04101C]">Just exploring</option>
                  </select>
                </div>

                {/* Error Message */}
                {error && (
                  <p className="text-[#FF6B7A] text-sm text-center">{error}</p>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full mt-2 px-6 py-4 rounded-xl bg-gradient-to-r from-[#00C4CC] via-[#00E08F] to-[#FFF7AE] text-[#04101C] font-bold text-lg hover:shadow-[0_0_30px_rgba(0,224,143,0.4)] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={20} />
                      Submit
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            /* Success State */
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-[#00C4CC] to-[#00E08F] flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={40} className="text-[#04101C]" />
              </div>
              <h2 className="text-2xl font-bold text-[#F5FDFF] mb-2">
                Thank You!
              </h2>
              <p className="text-[#F5FDFF]/60 mb-6">
                We&apos;ve received your details. Our team will reach out to you within 24 hours.
              </p>
              <button
                onClick={onClose}
                className="px-8 py-3 rounded-xl border-2 border-[#00E08F] text-[#00E08F] font-semibold hover:bg-[#00E08F]/10 transition-all duration-300"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactFormModal;
